import React, { useState } from "react";
import { NativeSyntheticEvent, StyleSheet, TextInputKeyPressEventData } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { ActivityIndicator, Avatar, Button, Paragraph, TextInput, useTheme } from "react-native-paper";
import { Block } from "@block";
import { useTranslation } from "react-i18next";
import { spacing } from "@styles/spacing";
import { QueueSpacing } from "@components/spacing";
import { testProps } from "app/shared/util/test-props";
import { InputText } from "app/shared/components/form/input";
import { RegisterOptions, useForm } from "react-hook-form";
import { ValidatePassword } from "app/shared/util/validate";
import { ChangePasswordState, FormChangePasswordData } from "./reducer/types";
import { getUserProfileKeyCloakSelector } from "app/modules/login/reducer/selectors";

enum FormEnum {
  passwordCurrent,
  passwordNew,
  passwordNewConfirm,
}

interface FormChangePasswordProps {
  isForceChange: boolean;
  changePassStore: ChangePasswordState;
  requestChange: Function;
  closeModal: Function;
  noteChange: string;
}

type ValidDataSchemeFormData = {
  [key in keyof typeof FormEnum]: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

function ContentChangePassword(props: FormChangePasswordProps) {
  const { isForceChange, changePassStore, closeModal, requestChange, noteChange = "" } = props;
  const dispatchStore = useDispatch();
  // setup error state for displaying error messages
  const { t } = useTranslation();
  const { colors, fonts } = useTheme();
  const buttonCancel = React.createRef<any>();
  const buttonNext = React.createRef<any>();

  const userProfileKeyCloak = useSelector(getUserProfileKeyCloakSelector);

  const { control, handleSubmit, getValues, trigger, setFocus } = useForm<FormChangePasswordData>({
    defaultValues: {
      passwordCurrent: "",
      passwordNew: "",
      passwordNewConfirm: "",
    },
    mode: "onChange",
  });

  const validateSchemeForm: ValidDataSchemeFormData = {
    passwordCurrent: {
      required: {
        value: true,
        message: t("SETTING_LOI_CHUA_NHAP_MAT_KHAU_HIEN_TAI"),
      },
    },
    passwordNew: {
      required: {
        value: true,
        message: t("SETTING_LOI_CHUA_NHAP_MAT_KHAU_MOI"),
      },
      validate: {
        double: (value) => {
          if (value === getValues("passwordCurrent") || value === userProfileKeyCloak?.username) {
            return t("SETTING_LOI_MAT_KHAU_BI_TRUNG").toString();
          }
          return true;
        },
        rules: (value) => {
          if (!ValidatePassword(value)) {
            return t("SETTING_LOI_MAT_KHAU_KHONG_HOP_LE").toString();
          }
          return true;
        },
      },
    },
    passwordNewConfirm: {
      required: {
        value: true,
        message: t("SETTING_LOI_CHUA_NHAP_LAI_MAT_KHAU_MOI"),
      },
      validate: {
        confirm: (value) => {
          if (value !== getValues("passwordNew")) {
            return t("SETTING_LOI_NHAP_SAI_MAT_KHAU_CONFIRM").toString();
          }
          return true;
        },
        rules: (value) => {
          if (!ValidatePassword(value)) {
            return t("SETTING_LOI_MAT_KHAU_KHONG_HOP_LE").toString();
          }
          return true;
        },
      },
    },
  };

  const [secureCurrent, setValueSecureCurrent] = useState<boolean>(true);
  const [secureNew, setValueSecureNew] = useState<boolean>(true);
  const [secureNewConfirm, setValueSecureNewConfirm] = useState<boolean>(true);

  React.useEffect(() => {}, [dispatchStore]);

  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        textInput: {
          color: colors.accent,
          backgroundColor: colors.backgroundForm,
          fontWeight: "700",
          textShadowColor: "black",
          fontSize: 12,
        },
        buttonForm: { width: 128, alignSelf: "center", backgroundColor: colors.backgroundForm },
        buttonText: { ...fonts.medium },
        textField: { ...fonts.bold, color: colors.text, fontSize: 16, textAlign: "center", paddingBottom: spacing.small },
        textNote: {
          ...fonts.regular,
          color: colors.error,
          fontSize: 11,
          lineHeight: 16,
          textAlign: "center",
          paddingBottom: spacing.small,
          display: isForceChange ? "flex" : "none",
        },
        textTerm: { ...fonts.regular, color: colors.text, fontSize: 11, lineHeight: 16 },
        textError: {
          ...fonts.regular,
          color: colors.error,
          fontSize: 11,
          lineHeight: 16,
          textAlign: "center",
          paddingTop: spacing.small,
          display: changePassStore.statusCreate === "error" ? "flex" : "none",
        },
        loading: {
          marginVertical: spacing.large,
          display: changePassStore.statusCreate === "pending" ? "flex" : "none",
        },
      }),
    [isForceChange, changePassStore, fonts, colors, spacing],
  );

  const onPressRightPasswordShow = React.useCallback((type: number) => {
    if (type === 1) setValueSecureCurrent(false);
    else if (type === 2) setValueSecureNew(false);
    else setValueSecureNewConfirm(false);
  }, []);
  const onPressRightPasswordHide = React.useCallback((type: number) => {
    if (type === 1) setValueSecureCurrent(true);
    else if (type === 2) setValueSecureNew(true);
    else setValueSecureNewConfirm(true);
  }, []);

  const onBlurInput = (value: number) => {
    if (value === 0) {
      trigger("passwordCurrent");
    } else if (value === 1) {
      trigger("passwordNew");
      if (getValues("passwordNewConfirm") !== "") {
        trigger("passwordNewConfirm");
      }
    } else if (value === 2) {
      trigger("passwordNewConfirm");
    }
  };

  const handleRequestChangePassword = (value: FormChangePasswordData): void => {
    const data = {
      passwordCurrent: value.passwordCurrent,
      passwordNew: value.passwordNew,
      passwordNewConfirm: value.passwordNewConfirm,
    };
    requestChange(data);
  };

  const onPressCancel = () => {
    closeModal();
  };

  const onPressAccept = () => {
    handleSubmit(handleRequestChangePassword).call(() => {});
  };

  const onKeyDown = (e: NativeSyntheticEvent<TextInputKeyPressEventData>, name: string) => {
    if (e.nativeEvent.key === "Tab") {
      if (name === "passwordNew" || name === "passwordNewConfirm") {
        setFocus(name);
      }
    }
  };

  /**
   * MARK: - Navigation
   */

  const rightPass = (type: number) => {
    return (
      <TextInput.Icon
        color="rgba(0, 0, 0, 0.54)"
        name={
          type === 1
            ? secureCurrent
              ? "eye"
              : "eye-off"
            : type === 2
            ? secureNew
              ? "eye"
              : "eye-off"
            : secureNewConfirm
            ? "eye"
            : "eye-off"
        }
        size={24}
        onPressIn={() => onPressRightPasswordShow(type)}
        onPressOut={() => onPressRightPasswordHide(type)}
      />
    );
  };

  const FormContent = (
    <Block ph={spacing.medium} pt={spacing.small}>
      <Paragraph style={styles.textField}>{t("SETTING_THAY_DOI_MAT_KHAU")}</Paragraph>
      <Paragraph style={styles.textNote}>{noteChange}</Paragraph>
      <Block bg={colors.backgroundForm} br={12} ph={spacing.medium} pb={spacing.medium}>
        <InputText
          name="passwordCurrent"
          control={control}
          rules={validateSchemeForm.passwordCurrent}
          inputProps={{ right: rightPass(1), secureTextEntry: secureCurrent }}
          style={styles.textInput}
          label={t("SETTING_NHAP_MAT_KHAU_HIEN_TAI")}
          onBlur={() => onBlurInput(0)}
          onKeyPress={(e) => onKeyDown(e, "passwordNew")}
        />
        <InputText
          name="passwordNew"
          control={control}
          rules={validateSchemeForm.passwordNew}
          inputProps={{ right: rightPass(2), secureTextEntry: secureNew }}
          style={styles.textInput}
          label={t("SETTING_NHAP_MAT_KHAU_MOI")}
          onBlur={() => onBlurInput(1)}
          onKeyPress={(e) => onKeyDown(e, "passwordNewConfirm")}
        />
        <InputText
          name="passwordNewConfirm"
          control={control}
          rules={validateSchemeForm.passwordNewConfirm}
          inputProps={{ right: rightPass(3), secureTextEntry: secureNewConfirm }}
          style={styles.textInput}
          label={t("SETTING_NHAP_LAI_MAT_KHAU_MOI")}
          onBlur={() => onBlurInput(2)}
        />
      </Block>
      <Block flex={1} middle center display={changePassStore.statusCreate === "pending" ? "flex" : "none"}>
        <ActivityIndicator color={colors.primary} size={"small"} style={styles.loading} />
      </Block>
      <Paragraph style={styles.textError}>{changePassStore.errorMsg}</Paragraph>
    </Block>
  );

  const FormNote = (
    <Block ph={spacing.medium} pt={spacing.medium}>
      <Block bg={colors.backgroundForm} br={12} p={spacing.medium}>
        <Paragraph style={styles.textTerm}>{t("SETTING_NOTE_DOI_MAT_KHAU")}</Paragraph>
        <Paragraph style={styles.textTerm}>{t("SETTING_RULES_CHANGE_PASS_1")}</Paragraph>
        <Paragraph style={styles.textTerm}>{t("SETTING_RULES_CHANGE_PASS_2")}</Paragraph>
        <Paragraph style={styles.textTerm}>{t("SETTING_RULES_CHANGE_PASS_3")}</Paragraph>
      </Block>
    </Block>
  );

  const ButtonRow = (
    <Block row middle pv={spacing.large}>
      <Block w={0} />
      <Button ref={buttonCancel} onPress={onPressCancel} mode="contained" style={styles.buttonForm}>
        {t("COMMON_QUAY_LAI")}
      </Button>
      <QueueSpacing size="large" />
      <Button ref={buttonNext} onPress={onPressAccept} mode="contained" {...testProps("nextBtn")}>
        {t("COMMON_TIEP_TUC")}
      </Button>
    </Block>
  );

  return (
    <Block>
      {FormContent}
      {FormNote}
      {ButtonRow}
    </Block>
  );
}

export default ContentChangePassword;
